import { Injectable } from '@angular/core';
import { HttpClient, HttpParams, HttpErrorResponse } from '@angular/common/http';
import { environment } from '@environments/environment';
import { ApiResponse } from '@app/_models';
import { Property } from '@app/_components/property';
import { retry, catchError, shareReplay } from 'rxjs/operators';
import { Observable, throwError } from 'rxjs';
import { AlertService } from '@app/_services';
import { SharedService } from './../_shared/shared.service';

@Injectable({ providedIn: 'root' })
export class PropertyService {

	wifiSecurities = [
		{ name: 'WPA', value: 'WPA' },
		{ name: 'WEP', value: 'WEP' },
		{ name: 'None', value: 'nopass' }
	];

	sitePriorities = [
		{ name: "", value: "" },
		{ name: 'Priority 1', value: 1 },
		{ name: 'Priority 2', value: 2 },
		{ name: 'Priority 3', value: 3 },
		{ name: 'Priority 4', value: 4 },
		{ name: 'Priority 5 (Default)', value: 5 },
		{ name: 'Priority 6', value: 6 },
		{ name: 'Priority 7', value: 7 },
		{ name: 'Priority 8', value: 8 },
		{ name: 'Priority 9', value: 9 },
		{ name: 'Priority 10', value: 10 },
	];

	constructor
		(
			private http: HttpClient,
			private alertService: AlertService,
			private sharedService: SharedService
		) { }

	urlEndpoint = 'properties';

	add(property: Property): Observable<ApiResponse> {
		console.log('posting... ' + JSON.stringify(property));
		return this.http.post<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}`, property);
	}

	update(property: any): Observable<ApiResponse> {
		return this.http.patch<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}`, property);
	}

	updateSitePriority(property: Property): Observable<ApiResponse> {
		return this.http.patch<ApiResponse>(`${environment.apiUrl}/guest-portal/properties`, property);
	}

	updateDescription(property: Property): Observable<ApiResponse> {
		return this.http.patch<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}/update-description`, property);
	}

	delete(propertyId: number): Observable<ApiResponse> {
		return this.http.delete<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}/${propertyId}`);
	}

	getDeletePreview(shortTermRentalId: number): Observable<ApiResponse> {
		return this.http.get<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}/${shortTermRentalId}/delete-preview`).pipe(retry(1));
	}

	safeDelete(shortTermRentalId: number): Observable<ApiResponse> {
		return this.http.delete<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}/${shortTermRentalId}/safe-delete`);
	}

	deleteCategory(shortTermRentalId: number, category: string): Observable<ApiResponse> {
		return this.http.delete<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}/${shortTermRentalId}/delete-category/${category}`);
	}

	findAll(): Observable<any> {
		return this.http.get<any>(`${environment.apiUrl}/${this.urlEndpoint}/find-all`).pipe(retry({ count: 2, delay: 1500 }));
	}
	getFilterData(): Observable<any> {
		return this.http.get<any>(`${environment.apiUrl}/${this.urlEndpoint}/filterData`).pipe(retry({ count: 2, delay: 1500 }));
	}

	findAllSummary(): Observable<ApiResponse> {
		return this.http.get<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}/summary`).pipe(shareReplay(1));
	}

	findAllSummaryWithStatusActive(): Observable<ApiResponse> {
		return this.http.get<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}/summary-active`).pipe(shareReplay(1));
	}

	findAllSummaryWithStatusActiveAndSuspended(): Observable<ApiResponse> {
		return this.http.get<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}/summary-active-suspended`).pipe(shareReplay(1));
	}

	propertySummaryForShortData(): Observable<ApiResponse> {
		return this.http.get<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}/summary-active-suspended-drop-down`).pipe(shareReplay(1));
	}

	findPropertySummaryData(): Observable<ApiResponse> {
		return this.http.get<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}/summary-data`).pipe(shareReplay(1));
	}

	propertySummaryIndividualPropertyList(): Observable<ApiResponse> {
		return this.http.get<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}/summary-active-suspended/single-unit`).pipe(shareReplay(1));
	}



	ownerListByProperty(shortTermRentalId): Observable<ApiResponse> {
		return this.http.get<ApiResponse>(`${environment.apiUrl}/owner-profiles/shortTermRental/`+shortTermRentalId).pipe(shareReplay(1));
	}

	findAllSummaryList(): Observable<ApiResponse> {
		return this.http.get<ApiResponse>(`${environment.apiUrl}/reports/property/summary-list`).pipe(shareReplay(1));
	}

	findAllProperties(): Observable<any> {
		return this.http.get<any>(`${environment.apiUrl}/all-properties`).pipe(retry({ count: 2, delay: 1500 }));
	}

	findByFilter(page: any, size: any, sortBy: any, sortOrder: any, multiSortMeta, globalFilter: any, filters: any): Observable<ApiResponse> {

		let params = this.sharedService.prepareParamsFromPrimeNgDataTable(page, size, sortBy, sortOrder, multiSortMeta, globalFilter, filters);
		return this.http.get<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}`, { params: params }).pipe(retry({ count: 2, delay: 1500 }));
	}

	findAgentListByFilter(page: any, size: any, sortBy: any, sortOrder: any, multiSortMeta, globalFilter: any, filters: any): Observable<ApiResponse> {

		let params = this.sharedService.prepareParamsFromPrimeNgDataTable(page, size, sortBy, sortOrder, multiSortMeta, globalFilter, filters);
		return this.http.get<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}/agent-list`, { params: params }).pipe(retry({ count: 2, delay: 1500 }));
	}

	findDescriptions(page: any, size: any, sortBy: any, sortOrder: any, multiSortMeta, globalFilter: any, filters: any): Observable<ApiResponse> {

		let params = this.sharedService.prepareParamsFromPrimeNgDataTable(page, size, sortBy, sortOrder, multiSortMeta, globalFilter, filters);
		return this.http.get<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}/descriptions`, { params: params }).pipe(retry({ count: 2, delay: 1500 }));
	}

	findById(id: number): Observable<ApiResponse> {
		return this.http.get<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}/${id}`).pipe(retry({ count: 2, delay: 1500 }));
	}

	findYieldMaxPropertyByExtSystemId(id: number): Observable<ApiResponse> {
		return this.http.get<ApiResponse>(`${environment.yieldMaxApiUrl}/${this.urlEndpoint}/ext-system-id/${id}`).pipe();
	}

	audit(id: number): Observable<ApiResponse> {
		return this.http.get<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}/${id}/audit`).pipe(retry({ count: 2, delay: 1500 }));
	}


	copyFeaturesFromProperty(copyProperty) {
		return this.http.post<ApiResponse>(`${environment.apiUrl}/property-features/copy-property-features`, copyProperty);
	}

	mergeFeaturesFromProperty(mergeProperty) {
		return this.http.post<ApiResponse>(`${environment.apiUrl}/property-features/merge-property-features`, mergeProperty);
	}

	autoCreatePropertyFeatures(shortTermRentalId) {
		return this.http.get<ApiResponse>(`${environment.apiUrl}/property-features/auto-create/${shortTermRentalId}`).pipe(retry(1));
	}

	getDynamicLongPropDescription(shortTermRentalId) {
		return this.http.get<ApiResponse>(`${environment.apiUrl}/properties/prop-long-desc/${shortTermRentalId}`).pipe(retry(1));
	}

	getDynamicLongPropHeadline(shortTermRentalId) {
		return this.http.get<ApiResponse>(`${environment.apiUrl}/properties/prop-long-headline/${shortTermRentalId}`).pipe(retry(1));
	}

	updateAllPropertiesDescription() {
		return this.http.get<ApiResponse>(`${environment.apiUrl}/properties/prop-long-desc/for-all-properties`).pipe(retry(1));
	}

	generateLosRates(shortTermRentalId) {
		return this.http.get<ApiResponse>(`${environment.apiUrl}/rate-detail-los/generate/${shortTermRentalId}`).pipe(retry(1));
	}

	syncPropertyToYieldMax(shortTermRentalId) {
		return this.http.get<ApiResponse>(`${environment.yieldMaxApiUrl}/sync/properties/wiki/${shortTermRentalId}`).pipe(retry(1));
	}

	generateAllLosRates() {
		return this.http.get<ApiResponse>(`${environment.apiUrl}/rate-detail-los/generate-all`).pipe(retry(1));
	}

	search(name: string) {
		// return this.http.get<ApiResponse>(`${ environment.apiUrl }/${ this.urlEndpoint }?name=${name}`).pipe(retry({ count: 2, delay: 1500 }));

		return this.http.get<any>(`${environment.apiUrl}/location-detail-search?name=${name}`)
			.toPromise()
			.then(res => <any>res.data)
			.then(data => { return data; });
	}

	copyToNewProperty(shortTermRentalId: number) {
		return this.http.get<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}/copy-to-new-property/${shortTermRentalId}`).pipe(retry(1));
	}

	getAllPropertyFeature(): Observable<ApiResponse> {
		return this.http.get<ApiResponse>(`${environment.apiUrl}/property-features/edit/str/list`).pipe(shareReplay(1));
	}

	refreshPropertyCache(shortTermRentalId: number): Observable<ApiResponse> {
		return this.http.post<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}/cache/refresh-db-s3/${shortTermRentalId}`, {});
	}

	prepareParamsFromPrimeNgDataTable(page: any, size: any, sortBy: any, sortOrder: any, multiSortMeta, globalFilter: any, filters: any) {
		try {
			console.log(
				'  |  page:' + page
				+ '  |  size:' + size
				+ '  |  sortBy:' + sortBy
				+ '  |  sortOrder:' + sortOrder
				+ '  |  multiSortMeta:' + JSON.stringify(multiSortMeta)
				+ '  |  globalFilter:' + globalFilter
				+ '  |  filters:' + JSON.stringify(filters)
			);

			let params = new HttpParams();

			if (!isNaN(page)) { params = params.append('page', page); }
			if (!isNaN(size)) { params = params.append('size', size); }
			if (sortBy) { params = params.append('sort', sortBy + ',' + (sortOrder === 1 ? 'asc' : 'desc')); }
			if (multiSortMeta) {
				// if sortMode="multiple" in primeng table then multiSortMeta is used for ALL sorting (even just one column.)
				// Otherwise, single sorting is used above.
				// event.multiSortMeta:[{"field":"lastName","order":1},{"field":"firstName","order":1}]
				for (let i = 0; i < multiSortMeta.length; i++) {
					let json = JSON.parse(JSON.stringify(multiSortMeta[i]));
					params = params.append('sort', json.field + ',' + (json.order === 1 ? 'asc' : 'desc'));
				}
			}
			// globalFilter works, but did not implemnt in Spring but not hard to write custom findAll and specify certain columns to look.
			if (globalFilter) { params = params.append('filter', globalFilter); }

			// multiselect or input or whatever... these work slick.
			if (filters) {
				// key:status val:{"value":["A"],"matchMode":"in"}
				for (let i = 0, keys = Object.keys(filters), ii = keys.length; i < ii; i++) {
					console.log('key:' + keys[i] + ' val:' + JSON.stringify(filters[keys[i]]));
					params = params.append(keys[i], filters[keys[i]].value);
				}
			}
			return params;
		} catch (error) {
			console.log('prepareParamsFromPrimeNgDataTable()' + error);
		}
	}
}
